// MoviePoster — beautiful CSS-only fallback when a real movie poster
// isn't available. Renders a "studio one-sheet" style card: colored
// gradient seeded by the title, big film name in heading type, year +
// director on the bottom row. Looks intentional, not broken — and
// because it's pure CSS, it never fails to load.
//
// Usage:
//   <MoviePoster title="Selma" year={2014} director="Ava DuVernay" />
//
// When you have a real poster URL, prefer <img src={posterUrl}> with
// this component as the onError fallback.
import React from "react";
import { FilmStrip } from "@phosphor-icons/react";

// A curated cinema palette — dignified, archival, never neon.
// Each pair is (bg-from, bg-to). Title hash picks one deterministically
// so adjacent cards look distinct but the grid stays cohesive.
const PALETTES = [
  ["#1b3a2e", "#2f5a48"],  // forest
  ["#3a1f1f", "#6b3030"],  // burgundy
  ["#1a2840", "#384e75"],  // night blue
  ["#3d2a1a", "#704530"],  // sepia
  ["#1d1d1d", "#3a3a3a"],  // charcoal
  ["#36181f", "#693141"],  // wine
  ["#1e2b1f", "#3d4f3e"],  // moss
  ["#2c1a3b", "#523070"],  // royal purple
  ["#3a2510", "#7a4f24"],  // amber-brown
  ["#1a3a3a", "#306060"],  // teal
];

function pickPalette(title = "") {
  // Sum char codes → index into palette. Stable across renders.
  const hash = [...title].reduce((a, c) => a + c.charCodeAt(0), 0);
  return PALETTES[Math.abs(hash) % PALETTES.length];
}

// Strip "(YYYY)" from titles like "Do the Right Thing (1989)" so the
// year doesn't appear twice on the card.
function cleanTitle(title) {
  return (title || "").replace(/\s*\(\d{4}\)\s*$/, "").trim();
}

export default function MoviePoster({
  title,
  year,
  director,
  className = "",
  variant = "card",   // 'card' (16:10) | 'tall' (2:3 — proper one-sheet)
}) {
  const [from, to] = pickPalette(title);
  const clean = cleanTitle(title);
  return (
    <div
      data-testid="movie-poster-fallback"
      className={`absolute inset-0 w-full h-full overflow-hidden ${className}`}
      style={{
        background: `linear-gradient(160deg, ${from} 0%, ${to} 100%)`,
      }}
    >
      {/* Grain — gives the card a 35mm film texture. SVG noise stays
          crisp at any size and adds no weight. */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")",
        }}
      />
      {/* Thin border lines like a studio poster — top "MPA" badge,
          bottom credit block — sells the cinema-card aesthetic. */}
      <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
        <span className="text-[8px] tracking-[0.32em] uppercase text-cream/60 font-bold">
          Holy Words · Films
        </span>
        <FilmStrip size={11} weight="bold" className="text-cream/50" />
      </div>

      {/* Title block — pushed slightly above center so the bottom
          credit lockup has room to breathe. */}
      <div className="absolute inset-0 flex flex-col justify-center px-5 md:px-6">
        <h3
          className="font-heading text-cream leading-[0.95] tracking-tight"
          style={{
            fontSize: clamp(clean, variant),
            textShadow: "0 2px 24px rgba(0,0,0,0.4)",
          }}
        >
          {clean}
        </h3>
      </div>

      {/* Bottom credit — director + year, separated by an em-dash, in
          the all-caps "studio credit" style. */}
      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-2 text-cream/85">
        <span className="text-[9px] tracking-[0.22em] uppercase font-bold truncate min-w-0">
          {director ? `Directed by ${director}` : "A Holy Words Selection"}
        </span>
        {year && (
          <span className="text-[9px] tracking-[0.22em] uppercase font-bold shrink-0">
            {year}
          </span>
        )}
      </div>

      {/* Bottom vignette so the title pops against busy gradients */}
      <div
        className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none"
        style={{ background: "linear-gradient(to top, rgba(0,0,0,0.45), transparent)" }}
      />
    </div>
  );
}

// Title length → display size. Long titles get smaller text so the
// whole name always fits on one or two lines, never clipping.
function clamp(title, variant) {
  const len = (title || "").length;
  if (variant === "tall") {
    if (len <= 10) return "2.4rem";
    if (len <= 18) return "1.95rem";
    if (len <= 30) return "1.55rem";
    return "1.2rem";
  }
  // 16:10 card variant — sized for the BlackAmerican grid.
  if (len <= 10) return "2.1rem";
  if (len <= 18) return "1.7rem";
  if (len <= 30) return "1.35rem";
  return "1.05rem";
}
